import { InfoIcon } from '@phosphor-icons/react';

import { Button, Text } from '@components';
import { ApiEndpoint, Dictionaries, FormType, InputMod, InputType, Translation } from '@enums';
import i18n from '@translations';
import type { EndpointType, FormConfig, FormConfigMapping } from '@types';
import { getOptionListFromCatalog } from '@utils';

interface GetCacheFormConfigParams {
  endpoint?: EndpointType;
  statusValue?: boolean;
  loading?: boolean;
  purgeDisabled?: boolean;
  openExtensionsModal?: () => void;
  handlePurgeCache?: () => void;
}

export const getCacheFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  loading = false,
  purgeDisabled = false,
  openExtensionsModal,
  handlePurgeCache,
  statusValue,
}: GetCacheFormConfigParams): FormConfig => {
  const tForm: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.cache.${key}`);

  const tButton: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:buttons.cache.${key}`);

  const CACHE_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.CdnCache,
      endpoint: ApiEndpoint.DEFAULT,
      inputList: [
        {
          name: 'cache_purge',
          inputType: InputType.Custom,
          label: tForm('purgeCache'),
          description: (
            <div className="flex flex-col gap-4">
              <Text $level={5} color="text-gray-400" className="font-light text-justify leading-4">
                {tForm('cacheFilesWillForceTheCDNToFetchTheLatestVersionsOfTheseFilesFromYourServer')}
              </Text>
              <div className="flex bg-blue-50 fill-blue-400 rounded-md p-3 text-blue-500">
                <InfoIcon weight="fill" className="mr-3 flex min-w-5 h-5" />
                <Text $level={5} className=" bg-blue-50 fill-blue-400 rounded-md" $customizeColor>
                  {tForm(
                    'clearingTheCacheMayTemporarilyDegradeYourWebsitesPerformanceAndProlongTheTimeItTakesToLoadFilesFromYourSourceServer',
                  )}
                </Text>
              </div>
            </div>
          ),
          withoutLabel: true,
          children: (
            <Button disabled={purgeDisabled} loading={loading} onClick={handlePurgeCache} className="w-full rounded-lg">
              {tButton('clearAll')}
            </Button>
          ),
        },
        {
          name: 'status',
          inputType: InputType.Switch,
          label: tForm('enabled'),
          description: tForm('togglePredefinedCacheRules'),
          fullWidth: true,
          centerItem: true,
          withoutLabel: true,
          disabled: loading,
        },
        {
          name: 'cache_mode',
          inputType: InputType.Select,
          inputMod: InputMod.Filled,
          label: tForm('cacheLevel'),
          description: tForm('determineHowMuchOfYourWebsitesStaticContentYouWant7BullToCache'),
          placeholder: tForm('pleaseSelectYourCacheLevel'),
          options: getOptionListFromCatalog({ name: Dictionaries.CachingLevelOption }),
          centerItem: true,
          withoutLabel: true,
          loading,
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
        {
          name: 'overwrite_cache_header',
          inputType: InputType.Select,
          inputMod: InputMod.Filled,
          label: tForm('cacheOverwriteHeader'),
          description: tForm(
            'specifyCacheOverwriteHeaderToControlCachingBehaviorEnsuringUpdatedContentIsServedPromptlyAndCachedVersionsAreBypassedAsNeeded',
          ),
          placeholder: tForm('pleaseSelectYourOverwriteHeader'),
          options: getOptionListFromCatalog({ name: Dictionaries.CacheOverwriteHeader }),
          centerItem: true,
          withoutLabel: true,
          loading,
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
        {
          name: 'server_cache_ttl',
          inputType: InputType.Select,
          inputMod: InputMod.Filled,
          label: tForm('serverCacheTimeToLive'),
          description: tForm(
            'duringThisPeriodRequestsForFilesAreServedFromTheServerCacheReducingLoadOnTheOriginServerAndSpeedingUpResponses',
          ),
          placeholder: tForm('pleaseSelectYourServerCache'),
          options: getOptionListFromCatalog({ name: Dictionaries.CacheTTLOption }),
          centerItem: true,
          withoutLabel: true,
          loading,
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
        {
          name: 'browser_cache_ttl',
          inputType: InputType.Select,
          inputMod: InputMod.Filled,
          label: tForm('browserCacheTimeToLive'),
          description: tForm('determineTheLengthOfTime7BullInstructsAVisitorsBrowserToCacheFiles'),
          placeholder: tForm('pleaseSelectYourBrowserCache'),
          options: getOptionListFromCatalog({ name: Dictionaries.CacheTTLOption }),
          centerItem: true,
          withoutLabel: true,
          loading,
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
        {
          name: 'extensions',
          inputType: InputType.Custom,
          label: tForm('cacheFileExtension'),
          description: tForm('configureWhichFileTypes7BullShouldCacheByEnteringDifferentFileExtensions'),
          withoutLabel: true,
          loading,
          children: (
            <Button
              onClick={openExtensionsModal}
              disabled={!statusValue || loading}
              loading={loading}
              className="rounded-lg"
            >
              {tButton('configure')}
            </Button>
          ),
          inactive: {
            field: 'status',
            condition: (value: boolean) => !value,
          },
        },
      ],
    },
  };

  return (CACHE_FORM[endpoint] ?? CACHE_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
